import { type FC, memo, useMemo } from 'react';
import { Trans } from 'react-i18next';
import type { GameV1 } from '@/api/games';
import { getAvailableRecommendedCurrency } from '@/components/GameItem/helpers';
import { ImageComponent } from '@/components/Image';
import { getGameCover } from '@/helpers/cdn';
import { useCMSGameCovers } from '@/modules/common/hooks/useCMSGameCovers';
import type { TCardMode } from '@/modules/game/types';
import { useGetCommonStore } from '@/store';
import { GameCardWrapper } from '../GameCardWrapper';
import {
  ScLockedGameCardIcon,
  ScLockedGameCardInner,
  ScLockedGameCardMessage,
} from './styled';

interface ILockedGameCardProps {
  game: GameV1;
  mode: TCardMode;
  withoutBottom?: boolean;
}

export const LockedGameCard: FC<ILockedGameCardProps> = memo(
  ({ game, mode, withoutBottom }) => {
    const { id } = game;
    const useCommonStore = useGetCommonStore();

    const wallets = useCommonStore.use.wallets();

    const { covers } = useCMSGameCovers();

    const availableCurrency = useMemo(
      () => getAvailableRecommendedCurrency(game.real, wallets),
      [game, wallets]
    );

    const url = useMemo(
      () => covers?.[id]?.cover?.url || getGameCover(id),
      [covers, id]
    );

    return (
      <GameCardWrapper game={game} mode={mode} withoutBottom={withoutBottom}>
        <ScLockedGameCardInner>
          <ImageComponent
            alt="cover"
            className="cover-image"
            fill
            loading="lazy"
            src={url}
          />
          <div className="content">
            <ScLockedGameCardIcon>
              <ImageComponent
                alt="play button icon"
                height={16}
                src="/game/locked-card-icon.svg"
                width={16}
              />
            </ScLockedGameCardIcon>
            <ScLockedGameCardMessage>
              <Trans
                components={{ 2: <strong /> }}
                i18nKey="noCurrency"
                ns="common"
                values={{ currency: availableCurrency }}
              />
            </ScLockedGameCardMessage>
          </div>
        </ScLockedGameCardInner>
      </GameCardWrapper>
    );
  }
);
